import { type AuthorInfo, listAuthors } from "@serophin/nottldr-shared";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { AuthorList } from "../../components/author/AuthorList";
import { CategoryFilter } from "../../components/explore/CategoryFilter";
import type { PageProps } from "../pageProps";
import { FILTER_CARD_CLASS } from "./ArticlesPage";
import { type AuthorsSeed, SEED } from "./seedTypes";
import { EXPLORE_TABS, useHeaderTabs } from "./tabs";
import { filterOf, useCategoryFilter } from "./useCategoryFilter";
import { useSeed } from "./useSeed";

type Page = { authors: AuthorInfo[]; nextCursor: string | null };

/** Pages of authors: the first from the prerender or from the filter, the rest from the cursor (which carries the filter). */
const useAuthorPages = (seed: AuthorsSeed | undefined, category: string, subcategory: string) => {
  const [page, setPage] = useState<Page>(seed ?? { authors: [], nextCursor: null });
  const [isLoading, setIsLoading] = useState(seed === undefined);
  const generation = useRef(0);
  const seededKey = useRef<string | undefined>(seed ? "all/all" : undefined);
  const key = `${category}/${subcategory}`;

  const run = useCallback(async (params: { cursor?: string; category?: string; subcategory?: string }, append: boolean) => {
    const current = ++generation.current;
    setIsLoading(true);
    try {
      const next = await listAuthors(params);
      if (current === generation.current) {
        setPage((previous) => ({ authors: append ? [...previous.authors, ...next.authors] : next.authors, nextCursor: next.nextCursor }));
      }
    } catch (error) {
      console.error("Failed to load authors", error);
    } finally {
      if (current === generation.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const skip = seededKey.current === key;
    seededKey.current = undefined;
    if (!skip) {
      setPage({ authors: [], nextCursor: null });
      void run(filterOf({ category, subcategory }), false);
    }
  }, [key]);

  const loadMore = () => {
    if (page.nextCursor && !isLoading) {
      void run({ cursor: page.nextCursor }, true);
    }
  };

  return { page, isLoading, loadMore };
};

/** Explore authors, in the day's shuffle, narrowed by category and subcategory. */
export const AuthorsPage = (_props: PageProps) => {
  const seed = useSeed<AuthorsSeed>(SEED.authors);
  const selection = useCategoryFilter();
  const { page, isLoading, loadMore } = useAuthorPages(seed, selection.category, selection.subcategory);
  useHeaderTabs(EXPLORE_TABS, "authors");

  return (
    <div>
      <div class={FILTER_CARD_CLASS}>
        <CategoryFilter
          selectedCategory={selection.category}
          selectedSubcategory={selection.subcategory}
          onCategoryChange={selection.selectCategory}
          onSubcategoryChange={selection.selectSubcategory}
        />
      </div>
      <AuthorList authors={page.authors} hasMore={page.nextCursor !== null} isLoading={isLoading} onLoadMore={loadMore} />
    </div>
  );
};
